import { useState, useContext, useEffect, useRef } from 'react';
import { UserEngagementContext } from '../../../utils/userEngagementContext';
import styles from './InactivityPopUp.module.css';
import PopUp from '../PopUp/PopUp';
import InputText from '../InputText/InputText';
import InputTextArea from '../InputTextArea/InputTextArea';
import InactivityImage from '../../../assets/inactivity-popup-image.webp';
import { CircularProgress } from '@mui/material';

const InactivityPopUp = ({ isOpen, onClose }) => {

    const { setLatestLeadTimestamp } = useContext(UserEngagementContext);
    const closeAnimationRef = useRef(null);

    const [form, setForm] = useState({
        email: '',
        question: ''
    });

    const [formErrors, setFormErrors] = useState({
        email: '',
        question: ''
    });

    /* States for submission status */
    const [loading, setLoading] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm((prevForm) => ({
            ...prevForm,
            [name]: value,
        }));
    };

    const handleClearError = (e) => {
        const { name } = e.target;
        setFormErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
    };

    const validateEmail = () => {
        const email = form.email;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setFormErrors(prevErrors => ({ ...prevErrors, email: 'Invalid email format' }));
            return false;
        }
        return true;
    };

    const handleSubmit = () => {
        if (!validateEmail(form.email)) return;

        setLoading(true);

        const data = {
            email: form.email,
            question: form.question,
            tags: ['inactivity popup']
        };

        const url = process.env.REACT_APP_TB2_API_URL + '/events/lead';
        const body = JSON.stringify(data);

        // Update context from the beginning of submission (optimistic update)
        setLatestLeadTimestamp(new Date().toISOString().slice(0, 10));

        // Fetch and let the result be handled silently
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: body,
        })

        setLoading(false);
        setSubmitSuccess(true);
    };

    useEffect(() => {
        if (!submitSuccess && !submitError) return;

        const timer = setTimeout(() => {
            if (closeAnimationRef.current) {
                closeAnimationRef.current();
            } else {
                onClose();
            }
        }, 2000);
        return () => clearTimeout(timer);

    }, [submitSuccess, submitError]);

    return (
        isOpen && (
            <PopUp
                maxWidth={930}
                close={onClose}
                closeButton
                closeWithBackground
                className={styles.popUpBodyContainer}
                headerSize={63}
                >
                    {({ closeAnimation }) => {
                        closeAnimationRef.current = closeAnimation;
                        return (
                            <div className={styles.content}>
                                <div className={styles.textContainer}>
                                    <span className={styles.title}>
                                        Stay in the
                                        <span className={styles.titleHighlight}> Loop </span>
                                        with Exclusive Offers
                                    </span>
                                    <span className={styles.description}>
                                            Sign up with your email to get insider updates, special promotions, and the latest news delivered straight to your inbox.
                                    </span>
                                    <div className={styles.form}>
                                        <InputText
                                            name="email"
                                            placeholder="Enter your email"
                                            value={form.email}
                                            onChange={handleInputChange}
                                            error={formErrors.email}
                                            onFocus={handleClearError}
                                            onBlur={validateEmail}
                                            className={styles.emailInput}
                                        />
                                        {loading && (
                                            <CircularProgress className={styles.loadingSpinner} />
                                        )}
                                        {!loading && !submitSuccess && !submitError && (
                                            <button
                                                type="button"
                                                className={styles.submitButton}
                                                onClick={handleSubmit}
                                            >
                                                Get My Exclusive Updates
                                            </button>
                                        )}
                                        {submitSuccess && !loading && (
                                            <span className={styles.successMessage}>
                                                Thank you! You've been subscribed successfully.
                                            </span>
                                        )}
                                        {submitError && !loading && (
                                            <span className={styles.errorMessage}>
                                                There was an error submitting the form. Please try again later.
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className={styles.imageContainer}>
                                    <img src={InactivityImage} alt="Inactivity Image" className={styles.image} />
                                </div>
                            </div>
                        );
                    }}
            </PopUp>
        )
    );
};

export default InactivityPopUp;
